//* Importing react hooks
import { useEffect, useState } from "react";

//* Importing bootstrap components
import { Button, Form} from "react-bootstrap";

//* Importing react-router hooks
import { useNavigate, useParams } from "react-router-dom";

//* Importing icons
import { ArrowBack, SaveRounded } from "@material-ui/icons";

//* Importing moment
import moment from "moment";

//* Importing date-io
import {
  DatePicker,
  TimePicker,
  MuiPickersUtilsProvider,
} from "@material-ui/pickers";

//* Importing date-io
import MomentUtils from "@date-io/moment";


//! Importing custom hooks
import { useDemands } from "../../../hooks/demand/useDemands";
import { useDemandsTags } from "../../../hooks/demand/demandTags/useDemandTag";

//* Importing components
import { TagsSelector } from "../../../component/common/TagsSelector";
import { Attention } from "../../../component/base/Attention";


//? Importing types
import type {
  CreateDemandsInput,
  UpdateDemandsInput,
} from "../../../hooks/demand/types/types";

export function DemandForm({ editMode }: { editMode?: boolean }) {
  const navigate = useNavigate();
  const params = useParams<{ id: string }>();
  const token = localStorage.getItem("token") || "";

  const [formData, setFormData] = useState({
    name: "",
    desc: "",
    date: moment().format("YYYY-MM-DD"),
    time: moment().format("HH:mm"),
  });

  const [selectedTagIds, setSelectedTagIds] = useState<string[]>([]);
  const [originalTagIds, setOriginalTagIds] = useState<string[]>([]);
  const [demandTagRelations, setDemandTagRelations] = useState<
    Record<string, string>
  >({});

  const [errorMessages, setErrorMessages] = useState<string | null>(null);
  const [loadingData, setLoadingData] = useState(false);

  const {
    getDemandById,
    createDemand,
    updateDemand,
    isCreating,
    isUpdating,
    error: demandError,
  } = useDemands(token);

  const {
    createDemand: createDemandTag,
    deleteDemand: deleteDemandTag,
    isCreating: isCreatingDemandTag,
    isDeleting: isDeletingDemandTag,
    error: demandsTagsError,
  } = useDemandsTags(token);

  useEffect(() => {
    /**
     * Fetches demand data by ID if in edit mode and sets the form data accordingly.
     * 
     * @async
     * @function fetchData
     * @returns {Promise<void>} A promise that resolves when the data fetching and state updates are complete.
     * 
     * @throws Will throw an error if the demand data is not found.
     * 
     * @description
     * This function performs the following steps:
     * 1. Checks if the component is in edit mode and if the `params.id` is available.
     * 2. Sets the loading state to true.
     * 3. Attempts to fetch the demand data by ID using the `getDemandById` function.
     * 4. If the demand data is not found, throws an error.
     * 5. Formats the date and time from the fetched demand data.
     * 6. Updates the form data state with the fetched and formatted data.
     * 7. Extracts and sets the selected tag IDs from the fetched demand data.
     * 8. Sets the original tag IDs for comparison purposes.
     * 9. Creates and sets a mapping of tag IDs to demand tag IDs.
     * 10. Handles any errors that occur during the data fetching process.
     * 11. Sets the loading state to false once the process is complete.
     * 
     * @example
     * // Example usage:
     * useEffect(() => {
     *   fetchData();
     * }, [editMode, params.id]);
     */
    const fetchData = async () => {
      if (editMode && params.id) {
        setLoadingData(true);
        try {
          const demandData = await getDemandById(params.id);
          if (!demandData) {
            throw new Error("Demand data not found");
          }

          const formattedDateTime = moment(demandData.date).format(
            "YYYY-MM-DD HH:mm:ss"
          );
          const [datePart, timePart] = formattedDateTime.split(" ");

          setFormData({
            name: demandData.name || "",
            desc: demandData.desc || "",
            date: datePart,
            time: moment(timePart, "HH:mm:ss").format("H:mm"),
          });

          const tagIds = demandData.tags
            ? demandData.tags.map((t) => t.id)
            : [];
          setSelectedTagIds(tagIds);
          setOriginalTagIds(tagIds);

          const tagRelations: Record<string, string> = {};
          if (demandData.tags) {
            demandData.tags.forEach((t) => {
              if (t.demandTagId) {
                tagRelations[t.id] = t.demandTagId;
              }
            });
          }
          setDemandTagRelations(tagRelations);
        } catch (err) {
          console.error("Erro ao buscar dados da demanda:", err);
          setErrorMessages("Não foi possível carregar os dados da demanda.");
        } finally {
          setLoadingData(false);
        }
      }
    };

    fetchData();
  }, [editMode, params.id, getDemandById]);

  /**
   * Validates the provided name.
   *
   * This function checks if the given name has at least 3 characters after trimming.
   * If the name is too short, it sets an error message and returns false.
   * Otherwise, it clears any error messages and returns true.
   *
   * @param name - The name to be validated.
   * @returns A boolean indicating whether the name is valid.
   */
  const validateName = (name: string): boolean => {
    if (name.trim().length < 3) {
      setErrorMessages("O nome deve ter pelo menos 3 caracteres.");
      return false;
    } else {
      setErrorMessages(null);
      return true;
    }
  };

  /**
   * Handles the form submission for creating or updating a demand.
   *
   * @param {React.FormEvent} e - The form event triggered on submission.
   * @returns {Promise<void>} - A promise that resolves when the form submission is complete.
   *
   * @remarks
   * This function performs the following steps:
   * 1. Prevents the default form submission behavior.
   * 2. Validates the name field using the `validateName` function.
   * 3. Checks if the date and time fields are filled.
   * 4. Constructs the payload for creating or updating a demand.
   * 5. Logs the payload to the console.
   * 6. Attempts to create or update the demand using the appropriate API functions.
   * 7. Manages the addition and removal of tags associated with the demand.
   * 8. Navigates to the demands page upon successful submission.
   * 9. Catches and logs any errors that occur during the process.
   *
   * @throws Will set an error message if the name validation fails, or if the date and time fields are not filled.
   * @throws Will log an error message if the demand creation or update fails.
   */
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const isNameValid = validateName(formData.name);
    if (!isNameValid) {
      return;
    }

    if (!formData.date || !formData.time) {
      setErrorMessages("Data e horário são obrigatórios.");
      return;
    }

    const payload: CreateDemandsInput | UpdateDemandsInput =
      editMode && params.id
        ? {
            id: params.id,
            name: formData.name.trim(),
            desc: formData.desc.trim(),
            date: moment(formData.date, "YYYY-MM-DD").format("YYYY-MM-DD"),
            time: moment(formData.time, "H:mm").format("HH:mm"),
          }
        : {
            name: formData.name.trim(),
            desc: formData.desc.trim(),
            date: moment(formData.date, "YYYY-MM-DD").format("YYYY-MM-DD"),
            time: moment(formData.time, "H:mm").format("HH:mm"),
          };


    try {
      let demandId = params.id || "";
      let demandResult: any = null;

      if (editMode && params.id) {
        demandResult = await updateDemand(payload as UpdateDemandsInput);
        demandId = params.id;
      } else {
        demandResult = await createDemand(payload as CreateDemandsInput);
        demandId = demandResult?.id;
      }

      const newTagIds = selectedTagIds;
      const oldTagIds = originalTagIds;

      // Tags adicionadas
      const addedTags = newTagIds.filter((t) => !oldTagIds.includes(t));

      // Tags removidas
      const removedTags = oldTagIds.filter((t) => !newTagIds.includes(t));

      for (const tagId of addedTags) {
        await createDemandTag({ demand_id: demandId, tag_id: tagId });
      }

      for (const tagId of removedTags) {
        const demandTagId = demandTagRelations[tagId];
        if (demandTagId) {
          await deleteDemandTag(demandTagId);
        }
      }

      navigate("/demands");
    } catch (err: any) {
      console.error("Operação falhou:", err);
      if (!errorMessages) {
        setErrorMessages("Ocorreu um erro ao salvar a demanda.");
      }
    }
  };

  const isLoading =
    isCreating ||
    isUpdating ||
    loadingData ||
    isCreatingDemandTag ||
    isDeletingDemandTag;

  return (
    <div >
      <div className="col-12 ">
        <div className="py-5 px-3">
          <div className="card-body" id="card" style={{ overflow: "visible" }}>
            <h4
              className="mt-0 header-title"
              style={{ fontSize: "1.5rem", color: "black" }}
            >
              {editMode ? "Editar" : "Cadastrar"} Demanda
            </h4>
            <p style={{ fontSize: "1rem", color: "black" }}>
              {editMode
                ? "Edite uma demanda do seu sistema"
                : "Cadastre as demandas do seu sistema"}
            </p>
            <br />

            {!isLoading && (
              <Form onSubmit={handleSubmit}>
                <Form.Group className="mb-3">
                  <Form.Label>
                    Nome <Attention />
                  </Form.Label>
                  <Form.Control
                    placeholder="Nome da demanda"
                    value={formData.name}
                    onChange={(e) => {
                      setFormData({ ...formData, name: e.target.value });
                      validateName(e.target.value);
                    }}
                    isInvalid={!!errorMessages}
                    required
                  />
                  <Form.Control.Feedback type="invalid">
                    {errorMessages}
                  </Form.Control.Feedback>
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>Descrição</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={3}
                    placeholder="Descrição da demanda"
                    value={formData.desc}
                    onChange={(e) =>
                      setFormData({ ...formData, desc: e.target.value })
                    }
                  />
                </Form.Group>

                <div className="d-flex justify-content-between">
                  <Form.Group className="mb-3 me-2" style={{ flex: 1 }}>
                  <Form.Label>
                    Data de encerramento <Attention />
                  </Form.Label>
                  <MuiPickersUtilsProvider utils={MomentUtils}>
                    <DatePicker
                    autoOk
                    margin="normal"
                    style={{ width: "100%" }}
                    invalidDateMessage="Data inválida"
                    minDate={moment()}
                    cancelLabel="Cancelar"
                    clearLabel="Limpar"
                    format="DD/MM/YYYY"
                    value={moment(formData.date, "YYYY-MM-DD")}
                    onChange={(date) =>
                      setFormData({
                      ...formData,
                      date: date
                        ? moment(date).format("YYYY-MM-DD")
                        : moment().format("YYYY-MM-DD"),
                      })
                    }
                    />
                  </MuiPickersUtilsProvider>
                  </Form.Group>

                  <Form.Group className="mb-3 ms-2" style={{ flex: 1 }}>
                  <Form.Label>
                    Horário de encerramento <Attention />
                  </Form.Label>
                  <MuiPickersUtilsProvider utils={MomentUtils}>
                    <TimePicker
                    autoOk
                    margin="normal"
                    style={{ width: "100%" }}
                    invalidDateMessage="Horário inválido"
                    ampm={false}
                    cancelLabel="Cancelar"
                    clearLabel="Limpar"
                    value={moment(formData.time, "H:mm")}
                    onChange={(time) =>
                      setFormData({
                      ...formData,
                      time: time
                        ? time.format("H:mm")
                        : moment().format("H:mm"),
                      })
                    }
                    />
                  </MuiPickersUtilsProvider>
                  </Form.Group>
                </div>

                <Form.Group className="mb-3">
                  <TagsSelector
                    selectedTagIds={selectedTagIds}
                    onChange={(newSelectedTagIds) =>
                      setSelectedTagIds(newSelectedTagIds)
                    }
                  />
                </Form.Group>

                <div className="w-100 d-flex justify-content-between mt-5">
                  <Button
                    variant="secondary"
                    className="d-flex align-items-center"
                    onClick={() => navigate("/demands")}
                    type="button"
                  >
                    <ArrowBack className="me-2" /> Voltar
                  </Button>
                  <Button
                    variant="success"
                    type="submit"
                    disabled={isLoading}
                    className="d-flex align-items-center"
                  >
                    <SaveRounded className="me-2" /> Salvar
                  </Button>
                </div>
              </Form>
            )}

          </div>
        </div>
      </div>
    </div>
  );
}
